'use client';

import { HomePageBanner, HomePageCategory } from '@/models/HomePage';
import { isFuture, parseISO } from 'date-fns';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import OptimizedBannerListCarousel from './OptimizedBannerListCarousel';
import SmartImage from './SmartImage';
import { ScrollArea } from './ui/scroll-area';

export default function BannerlistWithCategoryList({
  banners,
  categories = [],
  categoriesTitle,
  dir,
  isPriority = false,
}: {
  banners?: HomePageBanner[];
  categories?: HomePageCategory[];
  categoriesTitle: string;
  dir: string;
  isPriority?: boolean;
}) {
  const [bannersFiltered, setBannersFiltered] = useState<HomePageBanner[]>(banners || []);

  useEffect(() => {
    if (banners) {
      const match = banners.filter(
        (banner) => banner?.badgeEndTime && isFuture(parseISO(banner.badgeEndTime)),
      );
      if (match.length > 0) {
        setBannersFiltered([match[0]]);
      } else {
        setBannersFiltered(banners);
      }
    }
  }, []);

  const CategoryItem = ({
    category,
    className,
  }: {
    category: HomePageCategory;
    className?: string;
  }) => (
    <Link
      href={category.url || ''}
      className={`flex items-center gap-4 hover:bg-hzc_beige_300 transition-colors duration-200 rounded-lg ${className}`}
    >
      <div className="relative w-6 h-6 flex-shrink-0">
        <SmartImage
          src={category.iconUrl || ''}
          alt={`${category.title} - Kategorisi` || `kategori - ${category.id} Kategorisi`}
          fill
          sizes="48px"
          quality={60}
          loading="lazy"
          className="object-cover rounded-lg"
          imageType="icon"
        />
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <h2 className="font-medium text-sm text-hzc_purple_500 truncate">{category.title}</h2>
      </div>
    </Link>
  );

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-[1fr_minmax(220px,25%)] gap-4 max-w-7xl w-full mx-auto md:items-stretch md:min-h-[532px]"
      dir={dir}
    >
      {/* Banner: sağ kutu yüksekliğine stretch (object-cover) */}
      <div className="relative w-full md:min-h-[532px]">
        <div className="w-full md:absolute md:inset-0 md:h-full">
          <OptimizedBannerListCarousel
            banners={bannersFiltered}
            isPriority={isPriority}
            fillHeight
          />
        </div>
      </div>

      {categories?.length > 0 && (
        <div className="hidden md:flex flex-col w-full rounded-2xl border bg-hzc_beige_400 gap-2 self-stretch md:min-h-[532px] md:max-h-[532px]">
          <div className="font-semibold text-left text-xl text-hzc_purple_500 px-2 pt-4 shrink-0">
            {categoriesTitle}
          </div>

          <ScrollArea className="flex-1 min-h-0 max-h-[450px]">
            <div className="flex flex-col relative pb-2">
              {categories.map((category) => (
                <CategoryItem key={category.id} category={category} className="py-4 px-4" />
              ))}
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-hzc_beige_400 to-transparent pointer-events-none" />
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
